/**
 * Hosted Agent Service
 *
 * API calls for creating, managing, and diagnosing hosted agents in Azure AI Foundry.
 * All methods are stateless — they take tokens and parameters, return typed results.
 *
 * Token audiences:
 * - ARM token (management.azure.com) — subscriptions, RBAC
 * - Foundry token (ai.azure.com) — agent management, invocations
 * - CogServices token (cognitiveservices.azure.com) — model deployments
 */

import { getArmBaseUrl } from './armConfig';
import { getLogicEndpoint } from './endpoints';
import type {
  AiAccount,
  FoundryProject,
  OpenAIDeployment,
  AgentVersionResponse,
  HealthCheckResponse,
  DebugAuthResponse,
  HostedAgentConnection,
} from '@/types/hostedAgent';
import {
  ROLE_COGNITIVE_SERVICES_OPENAI_USER,
  ROLE_AZURE_AI_DEVELOPER,
  ROLE_AZURE_AI_USER,
  FOUNDRY_FEATURES_HEADER,
} from '@/types/hostedAgent';

const ARM_BASE_URL = getArmBaseUrl();
const REQUEST_TIMEOUT = 60000;

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Hosts we will attach a bearer token to. `projectEndpoint` / `invocationsUrl`
 * come from connections.json (user-/backend-authored), so a tampered value could
 * otherwise exfiltrate a token to an arbitrary host. Enforce a strict host
 * allowlist before every authenticated request: the ARM control plane (RBAC
 * calls), the Otto data-plane (auto.{region}.logic.azure.com — app-identity
 * lookup), and the legitimate Foundry data-plane hosts.
 */
const ALLOWED_HTTPS_HOST_SUFFIXES = [
  '.services.ai.azure.com',
  '.openai.azure.com',
  '.cognitiveservices.azure.com',
  '.logic.azure.com',
] as const;

/** Trusted first-party base URLs whose origin is allowed as-is (covers the
 *  http://localhost mocks in E2E mode without loosening the https requirement
 *  for connections.json-sourced hosts). ARM comes from a constant; the Logic
 *  endpoint is resolved per-call since it depends on the selected region. */
function trustedOrigins(): Set<string> {
  const origins = new Set<string>();
  const add = (base: string | undefined) => {
    if (!base) return;
    try { origins.add(new URL(base).origin.toLowerCase()); } catch { /* ignore */ }
  };
  add(ARM_BASE_URL);
  add(getLogicEndpoint());
  return origins;
}

/**
 * Assert a URL targets an allowlisted host before we attach a token. Throws for
 * tampered / unexpected endpoints so credentials can't leak. Relative URLs (the
 * dev Vite '/foundry-proxy') are same-origin and safe. Trusted first-party
 * origins (ARM / Logic endpoint, incl. localhost mocks) are allowed as-is; all
 * other allowed hosts must be https and match an allowlisted suffix.
 */
function assertAllowedTokenUrl(url: string): void {
  if (url.startsWith('/')) return;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    throw new Error('Invalid request URL.');
  }
  if (trustedOrigins().has(parsed.origin.toLowerCase())) return;
  const host = parsed.hostname.toLowerCase();
  const isAllowedSuffix = ALLOWED_HTTPS_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
  if (parsed.protocol !== 'https:' || !isAllowedSuffix) {
    throw new Error(`Refusing to send authenticated request to disallowed host: ${parsed.hostname}`);
  }
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit,
  timeout = REQUEST_TIMEOUT,
): Promise<Response> {
  // Guard every request: connections.json-sourced endpoints must resolve to an
  // allowlisted host (ARM or Foundry) before we attach the Authorization header.
  assertAllowedTokenUrl(url);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    // In dev mode, proxy Foundry API calls through Vite to avoid CORS
    const isDev = import.meta.env.DEV && import.meta.env.MODE !== 'test';
    const isFoundryUrl = url.includes('.services.ai.azure.com') || url.includes('.openai.azure.com');
    if (isDev && isFoundryUrl) {
      const headers = { ...(init.headers as Record<string, string>) };
      headers['X-Foundry-Target'] = url;
      return await fetch('/foundry-proxy', { ...init, headers, signal: controller.signal });
    }
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
}

function authHeaders(token: string): Record<string, string> {
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function foundryHeaders(token: string): Record<string, string> {
  return {
    ...authHeaders(token),
    'Foundry-Features': FOUNDRY_FEATURES_HEADER,
  };
}

// =============================================================================
// RESOURCE DISCOVERY (Wizard Steps 1-3)
// =============================================================================

/**
 * List AI accounts (CognitiveServices) in a subscription, filtered to AIServices kind.
 * Design doc: Step 2a
 */
export async function listAiAccounts(
  armToken: string,
  subscriptionId: string,
): Promise<AiAccount[]> {
  const url = `${ARM_BASE_URL}/subscriptions/${subscriptionId}/providers/Microsoft.CognitiveServices/accounts?api-version=2025-06-01`;
  const response = await fetchWithTimeout(url, { headers: authHeaders(armToken) });
  if (!response.ok) throw new Error(`Failed to list AI accounts: ${response.status}`);

  const data = await response.json() as { value: Array<{
    id: string; name: string; location: string; kind: string;
    properties?: { endpoints?: Record<string, string> };
  }> };

  return data.value
    .filter((a) => a.kind === 'AIServices')
    .map((a): AiAccount => {
      const rg = a.id.match(/resourceGroups\/([^/]+)/)?.[1] ?? '';
      const endpoints = a.properties?.endpoints;
      return {
        id: a.id,
        name: a.name,
        location: a.location,
        kind: a.kind,
        resourceGroup: rg,
        ...(endpoints ? {
          properties: {
            endpoints: {
              ...(endpoints['AI Foundry Portal'] ? { 'AI Foundry Portal': endpoints['AI Foundry Portal'] } : {}),
              ...(endpoints['OpenAI Language Model Instance API'] ? { 'OpenAI Language Model Instance API': endpoints['OpenAI Language Model Instance API'] } : {}),
            },
          },
        } : {}),
      };
    });
}

/**
 * List projects within an AI Foundry account via ARM API.
 * Uses ARM token instead of Foundry data plane token to avoid AADSTS65002
 * preauthorization issues with first-party apps.
 * Design doc: Step 2b
 */
export async function listFoundryProjects(
  armToken: string,
  _aiAccountName: string,
  accountResourceId: string,
): Promise<FoundryProject[]> {
  const url = `${ARM_BASE_URL}${accountResourceId}/projects?api-version=2025-06-01`;
  const response = await fetchWithTimeout(url, { headers: authHeaders(armToken) });
  if (!response.ok) throw new Error(`Failed to list projects: ${response.status}`);

  const data = await response.json() as { value: Array<{ name: string; properties?: { displayName?: string } }> };
  return data.value.map((p): FoundryProject => {
    // ARM returns nested resource names as "accountName/projectName" — extract the last segment
    const projectName = p.name.includes('/') ? p.name.split('/').pop()! : p.name;
    return {
      name: projectName,
      ...(p.properties?.displayName ? { displayName: p.properties.displayName } : {}),
    };
  });
}

/**
 * List model deployments from the AI account via ARM API.
 * Uses ARM token instead of CogServices data plane token to avoid AADSTS65002.
 * Design doc: Step 3
 */
export async function listModelDeployments(
  armToken: string,
  _openaiEndpoint: string,
  accountResourceId: string,
): Promise<OpenAIDeployment[]> {
  const url = `${ARM_BASE_URL}${accountResourceId}/deployments?api-version=2025-06-01`;
  const response = await fetchWithTimeout(url, { headers: authHeaders(armToken) });
  if (!response.ok) throw new Error(`Failed to list deployments: ${response.status}`);

  const data = await response.json() as { value: Array<{
    name: string;
    properties?: { model?: { name?: string; format?: string }; provisioningState?: string };
    sku?: { name: string; capacity: number };
  }> };

  return data.value
    .filter((d) => d.properties?.provisioningState === 'Succeeded')
    .map((d): OpenAIDeployment => ({
      id: d.name,
      model: d.properties?.model?.name ?? d.name,
      status: 'succeeded',
      ...(d.sku ? { sku: { name: d.sku.name, capacity: d.sku.capacity } } : {}),
    }));
}

// =============================================================================
// PERMISSION CHECK
// =============================================================================

/**
 * Check if the current user has permission to create agents on the given AI project.
 * Uses a lightweight GET on the agents endpoint — a 403/401 means insufficient permissions.
 */
export async function checkAgentPermissions(
  foundryToken: string,
  projectEndpoint: string,
): Promise<{ hasAccess: boolean; error?: string }> {
  try {
    const url = `${projectEndpoint}/agents?api-version=v1`;
    const response = await fetchWithTimeout(url, {
      method: 'GET',
      headers: foundryHeaders(foundryToken),
    });
    if (response.ok) return { hasAccess: true };
    if (response.status === 401 || response.status === 403) {
      const body = await response.json().catch(() => ({}));
      const message = (body as Record<string, Record<string, string>>)?.error?.message
        ?? 'You do not have permission to manage agents on this project. Ensure you have the "Azure AI Developer" role.';
      return { hasAccess: false, error: message };
    }
    // Other errors (404, 500) — don't block, let creation attempt proceed
    return { hasAccess: true };
  } catch {
    // Network errors — don't block
    return { hasAccess: true };
  }
}

// =============================================================================
// AGENT LIFECYCLE (Wizard Step 5)
// =============================================================================

interface CreateAgentVersionParams {
  projectEndpoint: string;
  agentName: string;
  imageTag: string;
  modelDeployment: string;
  openaiEndpoint: string;
  acrImage: string;
}

/**
 * Create an agent version in AI Foundry.
 * Design doc: Step 5b
 */
export async function createAgentVersion(
  foundryToken: string,
  params: CreateAgentVersionParams,
): Promise<AgentVersionResponse> {
  const url = `${params.projectEndpoint}/agents/${params.agentName}/versions?api-version=v1`;
  const body = {
    definition: {
      kind: 'hosted',
      container_protocol_versions: [{ protocol: 'invocations', version: '1.0.0' }],
      cpu: '1',
      memory: '2Gi',
      image: params.acrImage,
      environment_variables: {
        BYO_MODEL_DEPLOYMENT_NAME: params.modelDeployment,
        AZURE_OPENAI_ENDPOINT: params.openaiEndpoint,
      },
    },
  };

  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: foundryHeaders(foundryToken),
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errorBody = await response.text().catch(() => '');
    throw new Error(`Failed to create agent version (${response.status}): ${errorBody}`);
  }
  return response.json() as Promise<AgentVersionResponse>;
}

/**
 * Get the latest agent version by listing all versions and returning the most recent.
 * Design doc: Step 5c
 */
export async function getAgentVersion(
  foundryToken: string,
  projectEndpoint: string,
  agentName: string,
): Promise<AgentVersionResponse> {
  const url = `${projectEndpoint}/agents/${agentName}/versions?api-version=v1`;
  const response = await fetchWithTimeout(url, { headers: foundryHeaders(foundryToken) });
  if (!response.ok) throw new Error(`Failed to get agent versions: ${response.status}`);

  const data = await response.json() as { data: AgentVersionResponse[] };
  if (!data.data || data.data.length === 0) {
    throw new Error('No versions found for this agent');
  }
  // Return the latest version (highest version number)
  const sorted = data.data.sort((a, b) => Number(b.version) - Number(a.version));
  return sorted[0]!;
}

/**
 * List all versions of an agent. Routes through the shared (host-allowlisted)
 * fetch layer so the Foundry token is never sent to a tampered projectEndpoint.
 */
export async function listAgentVersions(
  foundryToken: string,
  projectEndpoint: string,
  agentName: string,
): Promise<AgentVersionResponse[]> {
  const url = `${projectEndpoint}/agents/${agentName}/versions?api-version=v1`;
  const response = await fetchWithTimeout(url, { headers: foundryHeaders(foundryToken) });
  if (!response.ok) throw new Error(`Failed to list agent versions: ${response.status}`);
  const data = await response.json() as { data: AgentVersionResponse[] };
  return data.data ?? [];
}

/**
 * List agent versions to get the blueprint identity.
 * Design doc: Step 5d
 */
export async function getBlueprintIdentity(
  foundryToken: string,
  projectEndpoint: string,
  agentName: string,
): Promise<string | null> {
  const url = `${projectEndpoint}/agents/${agentName}/versions?api-version=v1`;
  const response = await fetchWithTimeout(url, { headers: foundryHeaders(foundryToken) });
  if (!response.ok) throw new Error(`Failed to get blueprint identity: ${response.status}`);

  const data = await response.json() as { data: AgentVersionResponse[] };
  return data.data[0]?.blueprint?.principal_id ?? null;
}

/**
 * Discover instance identity via the debug_auth endpoint.
 * Design doc: Step 5e
 */
export async function getInstanceIdentity(
  foundryToken: string,
  projectEndpoint: string,
  agentName: string,
): Promise<string | null> {
  const url = `${projectEndpoint}/agents/${agentName}/endpoint/protocols/invocations?api-version=v1`;
  const body = { debug_auth: true, session_id: 'rbac-setup' };

  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: foundryHeaders(foundryToken),
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`Failed to get instance identity: ${response.status}`);

  const data = await response.json() as DebugAuthResponse;
  return data.none_cognitiveservices?.oid ?? data.instance_cognitiveservices?.oid ?? null;
}

/**
 * Delete an agent version.
 */
export async function deleteAgentVersion(
  foundryToken: string,
  projectEndpoint: string,
  agentName: string,
  version = 1,
): Promise<void> {
  const url = `${projectEndpoint}/agents/${agentName}/versions/${version}?api-version=v1`;
  const response = await fetchWithTimeout(url, {
    method: 'DELETE',
    headers: foundryHeaders(foundryToken),
  });
  if (!response.ok && response.status !== 404) {
    throw new Error(`Failed to delete agent version ${version}: ${response.status}`);
  }
}

/**
 * Delete all existing versions then create a fresh version.
 * This is the "recreate" flow for failed provisioning.
 */
export async function recreateAgentVersion(
  foundryToken: string,
  params: CreateAgentVersionParams,
): Promise<AgentVersionResponse> {
  // List existing versions and delete them all
  const listUrl = `${params.projectEndpoint}/agents/${params.agentName}/versions?api-version=v1`;
  const listResp = await fetchWithTimeout(listUrl, { headers: foundryHeaders(foundryToken) });
  if (listResp.ok) {
    const listData = await listResp.json() as { data: AgentVersionResponse[] };
    if (listData.data?.length) {
      await Promise.all(
        listData.data.map((v) =>
          deleteAgentVersion(foundryToken, params.projectEndpoint, params.agentName, Number(v.version)),
        ),
      );
    }
  }
  return createAgentVersion(foundryToken, params);
}

// =============================================================================
// RBAC MANAGEMENT (Steps 5a, 5f, 5g, Diagnostics)
// =============================================================================

/**
 * Assign an RBAC role to a principal on a scope.
 * Returns true if assigned, false if already exists (409).
 */
export async function assignRbacRole(
  armToken: string,
  scope: string,
  principalId: string,
  roleDefinitionId: string,
  subscriptionId: string,
): Promise<boolean> {
  const assignmentId = crypto.randomUUID();
  const url = `${ARM_BASE_URL}${scope}/providers/Microsoft.Authorization/roleAssignments/${assignmentId}?api-version=2022-04-01`;
  const body = {
    properties: {
      roleDefinitionId: `/subscriptions/${subscriptionId}/providers/Microsoft.Authorization/roleDefinitions/${roleDefinitionId}`,
      principalId,
      principalType: 'ServicePrincipal',
    },
  };

  const response = await fetchWithTimeout(url, {
    method: 'PUT',
    headers: authHeaders(armToken),
    body: JSON.stringify(body),
  });

  if (response.status === 409) return false; // Already assigned
  if (!response.ok) throw new Error(`Failed to assign RBAC role: ${response.status}`);
  return true;
}

/**
 * Check if a principal has a specific role on a scope.
 */
export async function checkRbacAssignment(
  armToken: string,
  scope: string,
  principalId: string,
  roleDefinitionId: string,
  _subscriptionId: string,
): Promise<boolean> {
  const url = `${ARM_BASE_URL}${scope}/providers/Microsoft.Authorization/roleAssignments?$filter=principalId eq '${principalId}'&api-version=2022-04-01`;
  const response = await fetchWithTimeout(url, { headers: authHeaders(armToken) });
  if (!response.ok) throw new Error(`Failed to check RBAC: ${response.status}`);

  const data = await response.json() as { value: Array<{ properties: { roleDefinitionId: string } }> };
  const expectedSuffix = `/roleDefinitions/${roleDefinitionId}`;
  return data.value.some((a) => a.properties.roleDefinitionId.endsWith(expectedSuffix));
}

/**
 * Assign a role only if the principal does not already have it.
 *
 * Agent blueprint/instance identities are special Entra agent-identity principal
 * types (agentIdentityBlueprintPrincipal / ServiceIdentity). Azure RBAC only
 * accepts them as role-assignment targets during a brief window right after the
 * identity is created — that is when the role gets granted, during initial
 * provisioning. Re-PUTing the same assignment afterward is rejected with
 * PrincipalTypeNotSupported (400) / 500. Reads (checkRbacAssignment) work
 * regardless of principal type, so check first and skip the redundant PUT when
 * the role is already present. This keeps re-verify / reprovision from failing
 * on an already-provisioned agent whose role was assigned in that window.
 *
 * A genuinely missing role still attempts the PUT and surfaces any failure.
 *
 * @returns true if newly assigned, false if it was already present (skipped).
 */
export async function assignRbacRoleIfMissing(
  armToken: string,
  scope: string,
  principalId: string,
  roleDefinitionId: string,
  subscriptionId: string,
): Promise<boolean> {
  const alreadyAssigned = await checkRbacAssignment(
    armToken, scope, principalId, roleDefinitionId, subscriptionId,
  ).catch(() => false);
  if (alreadyAssigned) {
    return false;
  }
  return assignRbacRole(armToken, scope, principalId, roleDefinitionId, subscriptionId);
}

/**
 * Remove all RBAC role assignments for a principal on a scope.
 * Lists assignments, then deletes each one. Safe if none exist.
 */
export async function removeRbacAssignments(
  armToken: string,
  scope: string,
  principalId: string,
): Promise<number> {
  const listUrl = `${ARM_BASE_URL}${scope}/providers/Microsoft.Authorization/roleAssignments?$filter=principalId eq '${principalId}'&api-version=2022-04-01`;
  const listResp = await fetchWithTimeout(listUrl, { headers: authHeaders(armToken) });
  if (!listResp.ok) return 0;

  const data = await listResp.json() as { value: Array<{ id: string }> };
  if (!data.value?.length) return 0;

  await Promise.all(
    data.value.map(async (assignment) => {
      const deleteUrl = `${ARM_BASE_URL}${assignment.id}?api-version=2022-04-01`;
      await fetchWithTimeout(deleteUrl, { method: 'DELETE', headers: authHeaders(armToken) });
    }),
  );
  return data.value.length;
}

/**
 * Build the ARM scope string for a CognitiveServices account.
 */
export function buildAccountScope(
  subscriptionId: string,
  resourceGroup: string,
  aiAccountName: string,
): string {
  return `/subscriptions/${subscriptionId}/resourceGroups/${resourceGroup}/providers/Microsoft.CognitiveServices/accounts/${aiAccountName}`;
}

// =============================================================================
// HEALTH CHECK (Flow 3)
// =============================================================================

/**
 * Run end-to-end health check against the agent container.
 * Uses Logic App MI token (Foundry audience) in production.
 * Design doc: Flow 3
 */
export async function runHealthCheck(
  foundryToken: string,
  invocationsUrl: string,
  expectedImageTag: string,
): Promise<HealthCheckResponse> {
  const body = {
    health_check: true,
    session_id: 'health-check',
    expected_image_version: expectedImageTag,
  };

  const response = await fetchWithTimeout(invocationsUrl, {
    method: 'POST',
    headers: foundryHeaders(foundryToken),
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      return {
        status: 'unhealthy',
        image_version: 'unknown',
        copilot_session: 'unknown',
        model_auth: `auth_failed: HTTP ${response.status}`,
      };
    }
    throw new Error(`Health check failed: ${response.status}`);
  }

  return response.json() as Promise<HealthCheckResponse>;
}

// =============================================================================
// DIAGNOSTICS (Flow 4)
// =============================================================================

export interface DiagnosticResults {
  agentStatus: 'active' | 'provisioning' | 'failed' | 'not_found';
  logicAppMiRbac: boolean;
  logicAppMiError?: string;
  instanceRbac: boolean;
  blueprintRbac: boolean;
  healthCheck: HealthCheckResponse | null;
  healthCheckError?: string;
}

/** Context needed to resolve the Logic App MI for diagnostics. */
export interface AppContext {
  ottoToken: string;
  projectName: string;
  appName: string;
}

/**
 * Resolve the Logic App managed-identity principal (object) ID via the data-plane
 * app API. Needed to (re)assign the "Logic App -> Foundry" roles from the client.
 * Returns undefined when the app has no system-assigned identity; throws when the
 * app-identity request itself fails.
 */
export async function getLogicAppMiOid(appContext: AppContext): Promise<string | undefined> {
  const { getLogicEndpoint, apiVersions } = await import('./endpoints');
  const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(appContext.projectName)}/applications/${encodeURIComponent(appContext.appName)}?api-version=${apiVersions.otto}`;
  const resp = await fetchWithTimeout(url, {
    headers: { Authorization: `Bearer ${appContext.ottoToken}`, 'Content-Type': 'application/json' },
  });
  if (!resp.ok) throw new Error(`Failed to fetch app identity (${resp.status})`);
  const appData = await resp.json() as { identity?: { principalId?: string } };
  return appData?.identity?.principalId;
}

/**
 * Run all 5 diagnostic checks for a hosted agent connection.
 */
export async function runDiagnostics(
  armToken: string,
  foundryToken: string,
  connection: HostedAgentConnection,
  appContext?: AppContext,
): Promise<DiagnosticResults> {
  const { configuration, identity, endpoints } = connection;
  const scope = buildAccountScope(
    configuration.subscriptionId,
    configuration.resourceGroup,
    configuration.aiAccountName,
  );

  // Check 1: Agent version status
  let agentStatus: DiagnosticResults['agentStatus'];
  try {
    const version = await getAgentVersion(
      foundryToken, endpoints.projectEndpoint, configuration.agentName,
    );
    agentStatus = version.status === 'active' ? 'active' :
      version.status === 'failed' ? 'failed' : 'provisioning';
  } catch {
    agentStatus = 'not_found';
  }

  // Check 2: Logic App MI → Foundry (Azure AI Developer role)
  let logicAppMiRbac = false;
  let logicAppMiError: string | undefined;

  if (identity.logicAppManagedIdentityConfigured) {
    logicAppMiRbac = true;
  } else if (appContext) {
    // Fetch the Logic App MI principal ID via data-plane API
    try {
      const { getLogicEndpoint, apiVersions } = await import('./endpoints');
      const url = `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(appContext.projectName)}/applications/${encodeURIComponent(appContext.appName)}?api-version=${apiVersions.otto}`;
      const resp = await fetchWithTimeout(url, {
        headers: { Authorization: `Bearer ${appContext.ottoToken}`, 'Content-Type': 'application/json' },
      });

      if (!resp.ok) {
        logicAppMiError = `Failed to fetch app identity (${resp.status})`;
      } else {
        const appData = await resp.json() as { identity?: { principalId?: string } };
        const principalId = appData?.identity?.principalId;

        if (!principalId) {
          logicAppMiError = 'Logic App managed identity not enabled';
        } else {
          // Invocation needs both roles (ML-workspace + CognitiveServices Foundry backends).
          const [devRbac, userRbac] = await Promise.all([
            checkRbacAssignment(armToken, scope, principalId, ROLE_AZURE_AI_DEVELOPER, configuration.subscriptionId).catch(() => false),
            checkRbacAssignment(armToken, scope, principalId, ROLE_AZURE_AI_USER, configuration.subscriptionId).catch(() => false),
          ]);
          logicAppMiRbac = devRbac && userRbac;
          if (!logicAppMiRbac) {
            logicAppMiError = 'Missing RBAC assignment';
          }
        }
      }
    } catch (err) {
      logicAppMiError = err instanceof Error ? err.message : 'Failed to check Logic App MI';
    }
  } else {
    logicAppMiError = 'App context not available';
  }

  // Checks 3-4: Instance & Blueprint RBAC (parallel)
  const [instanceRbac, blueprintRbac] = await Promise.all([
    identity.instancePrincipalId
      ? checkRbacAssignment(armToken, scope, identity.instancePrincipalId, ROLE_COGNITIVE_SERVICES_OPENAI_USER, configuration.subscriptionId)
          .catch(() => false)
      : Promise.resolve(false),
    identity.blueprintPrincipalId
      ? checkRbacAssignment(armToken, scope, identity.blueprintPrincipalId, ROLE_COGNITIVE_SERVICES_OPENAI_USER, configuration.subscriptionId)
          .catch(() => false)
      : Promise.resolve(false),
  ]);

  // Check 5: End-to-end health check
  let healthCheck: HealthCheckResponse | null = null;
  let healthCheckError: string | undefined;
  if (agentStatus === 'active') {
    try {
      healthCheck = await runHealthCheck(
        foundryToken, endpoints.invocationsUrl, configuration.imageTag,
      );
    } catch (err) {
      healthCheckError = err instanceof Error ? err.message : String(err);
    }
  }

  return {
    agentStatus, logicAppMiRbac, instanceRbac, blueprintRbac, healthCheck,
    ...(logicAppMiError != null ? { logicAppMiError } : {}),
    ...(healthCheckError != null ? { healthCheckError } : {}),
  };
}

/**
 * Fix all missing RBAC assignments for a hosted agent.
 */
export async function fixAllRbac(
  armToken: string,
  connection: HostedAgentConnection,
  logicAppMiOid?: string,
): Promise<void> {
  const { configuration, identity } = connection;
  const scope = buildAccountScope(
    configuration.subscriptionId,
    configuration.resourceGroup,
    configuration.aiAccountName,
  );

  const assignments: Promise<boolean>[] = [];

  if (logicAppMiOid) {
    // Both roles — see ROLE_AZURE_AI_DEVELOPER doc: neither covers both Foundry backends alone.
    assignments.push(
      assignRbacRoleIfMissing(armToken, scope, logicAppMiOid, ROLE_AZURE_AI_DEVELOPER, configuration.subscriptionId),
      assignRbacRoleIfMissing(armToken, scope, logicAppMiOid, ROLE_AZURE_AI_USER, configuration.subscriptionId),
    );
  }
  if (identity.instancePrincipalId) {
    assignments.push(
      assignRbacRoleIfMissing(armToken, scope, identity.instancePrincipalId, ROLE_COGNITIVE_SERVICES_OPENAI_USER, configuration.subscriptionId),
    );
  }
  if (identity.blueprintPrincipalId) {
    assignments.push(
      assignRbacRoleIfMissing(armToken, scope, identity.blueprintPrincipalId, ROLE_COGNITIVE_SERVICES_OPENAI_USER, configuration.subscriptionId),
    );
  }

  await Promise.all(assignments);
}
